/**
 * Sequential Template Component
 * Actions must be completed in order, with auto-skip logic
 */
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'

import { Badge } from '@/components/elements/badge'
import { Card, CardContent } from '@/components/elements/card'
import { DRILL_STATES } from '@/types/drillTimer'
import { cn } from '@/utils/helpers'

export function SequentialTemplate({ actions = [], onLog, records = [] }) {
  const [cardStates, setCardStates] = useState(new Array(actions.length).fill('default'))
  const [lastTappedIndex, setLastTappedIndex] = useState(-1)

  // Restore state from records when component mounts or records change
  useEffect(() => {
    const newStates = new Array(actions.length).fill('default')
    let lastIndex = -1

    actions.forEach((action, index) => {
      // Find the most recent record for this action
      const actionRecords = records.filter((r) => r.action === action.action)

      if (actionRecords.length > 0) {
        const latestRecord = actionRecords[actionRecords.length - 1]

        if (
          latestRecord.state === DRILL_STATES.STARTED ||
          latestRecord.state === DRILL_STATES.COMPLETED
        ) {
          newStates[index] = 'completed'
          lastIndex = index
        } else if (latestRecord.state === DRILL_STATES.SKIPPED) {
          newStates[index] = 'skipped'
          if (index > lastIndex) {
            lastIndex = index
          }
        }
      }
    })

    setCardStates(newStates)
    setLastTappedIndex(lastIndex)
  }, [actions, records])

  const handleActionClick = (index) => {
    const currentState = cardStates[index]

    // Can't click completed cards
    if (currentState === 'completed') return

    const newStates = [...cardStates]

    // Auto-skip logic: skip all actions between last tapped and current
    if (index !== lastTappedIndex + 1) {
      for (let i = lastTappedIndex + 1; i < index; i += 1) {
        if (cardStates[i] === 'default') {
          newStates[i] = 'skipped'
          onLog(actions[i].action, DRILL_STATES.SKIPPED)
        }
      }

      // Reset actions after current
      for (let i = index + 1; i < cardStates.length; i += 1) {
        newStates[i] = 'default'
      }
    }

    // Mark current action as completed
    newStates[index] = 'completed'
    onLog(actions[index].action, DRILL_STATES.STARTED)

    setCardStates(newStates)
    setLastTappedIndex(index)
  }

  const handleSkippedClick = (index) => {
    // Un-skip this action and reset all after it
    const newStates = [...cardStates]
    newStates[index] = 'default'

    // Reset all actions after this one
    for (let i = index + 1; i < cardStates.length; i += 1) {
      newStates[i] = 'default'
    }

    setCardStates(newStates)
  }

  const getCardStyle = (state) => {
    if (state === 'completed') return 'bg-green-50 border-green-500 border-2'
    if (state === 'skipped') return 'bg-red-50 border-red-500 border-2'
    return 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
  }

  return (
    <div className="h-full space-y-3 overflow-y-auto p-4">
      {actions.map((action, index) => {
        const state = cardStates[index]
        const canClick = state === 'default' || state === 'skipped'

        return (
          <Card
            key={action.id || `action-${index}`}
            className={cn(
              'transition-all duration-200',
              getCardStyle(state),
              canClick && 'hover:scale-[1.02]',
            )}
            onClick={() => {
              if (state === 'default') {
                handleActionClick(index)
              } else if (state === 'skipped') {
                handleSkippedClick(index)
              }
            }}
          >
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="text-lg font-medium">{action.action}</div>
              </div>

              {state === 'completed' && <Badge className="bg-green-600">Completed</Badge>}
              {state === 'skipped' && <Badge variant="destructive">Skipped</Badge>}
            </CardContent>
          </Card>
        )
      })}

      {actions.length === 0 && (
        <div className="text-muted-foreground py-8 text-center">
          No actions defined for this drill
        </div>
      )}
    </div>
  )
}

SequentialTemplate.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      action: PropTypes.string.isRequired,
    }),
  ),
  onLog: PropTypes.func.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      state: PropTypes.string,
    }),
  ),
}
